import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import { formatBool, formatDate, formatInventorySummaryType } from "../../../../utils";
import { TableField, PageData } from "../../../types";
import {
    GetLoadsRequest,
    LoadListFilters,
    LoadResource,
    LoadListItem
  } from "../../../../models/api/barcodeSystem/loads/get";
import Store from "../../../index";
import { rawApi, api } from "../../../../lib";
import { AxiosResponse } from "axios";
import { defaultLoad, defaultSummaryFilters } from "./helpers";
import BarcodeSystemModule from "@/store/modules/barcodeSystem";
import notifications from "@/lib/notifications";
import _ from "lodash";
import * as modals from "@/lib/modals";
import { BarcodeOrderListItem } from "../../../../models/api/barcodeSystem/barcodeOrders/get";
import { ConsolidateLoadsRequest } from "../../../../models/api/barcodeSystem/loads/consolidate";

const name = "load";
const barcodeSystemModuleState = getModule(BarcodeSystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class LoadModule extends VuexModule {
    public currentLoad: LoadResource = defaultLoad();
    public currentLoadOrders: BarcodeOrderListItem[] = [];
    public data?: LoadListItem[] = null;
    public filters: LoadListFilters = defaultSummaryFilters();
    public dataSortKey: string = null;
    public dataSortAscending: boolean = true;
    public pageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 50
    };
    public fields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "loadName", sortable: true, label: "Load Name", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "companyName", sortable: true, label: "Company", hidden: false },
        { key: "displayBillToCompanyId", sortable: true, label: "BillTo", hidden: false },
        { key: "statusDescription", sortable: true, label: "Status", hidden: false},
        { key: "orderCount", sortable: true, label: "Order Count", hidden: false},
        { key: "locked", sortable: true, label: "Locked By", hidden: false},
        { key: "importedDate", sortable: true, label: "Imported Date", formatter: formatDate, hidden: false, noWrap: true}
    ];
    public loading: boolean = false;

    @Action
    async getLoads(params: GetLoadsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetList();
      }
  
      let target = `/barcode/loads/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.filters) {
        if (this.filters.id) {
            target += `&id=${this.filters.id}`;
        }
        if (this.filters.companyIds) {
          this.filters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.filters.loadName) {
            target += `&loadName=${this.filters.loadName}`;
        }
        if (this.filters.isOpen) {
            target += `&isOpen=${this.filters.isOpen}`;
        }
        if (this.filters.isLocked) {
            target += `&isLocked=${this.filters.isLocked}`;
        }
        if (this.filters.showZeroOrders) {
          target += `&showZeroOrders=${this.filters.showZeroOrders}`
        }
        if (this.filters.importedDate) {
            const dateData = this.filters.importedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&importedDateStart=${dateData[0]}&importedDateEnd=${dateData[1]}`;
        }
        if (this.filters.facilityCodes) {
          this.filters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.filters.statuses) {
          this.filters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.filters.billToCompanyIds) {
          this.filters.billToCompanyIds.forEach(element => {
            target += `&billToCompanyIds=${element}`;
          });
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.dataSortKey) {
        target += `&sortField=${this.dataSortKey}&SortAscending=${
          this.dataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<LoadListItem[]>(target);
        this.setList(response);
        this.setCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async applyListSort(sort: string) {     
      this.setDataSort(sort);
      await this.getLoads({
          pageNumber: 1,
          pageSize: this.pageData.pageSize
      });
    }

    @Action
    toggleField(fieldChange) {
      this.setFieldStatus(fieldChange);
    }

    @Action
    async getLoad(loadId): Promise<LoadResource> {
      const response = await rawApi.get<LoadResource>(`/barcode/loads/${loadId}`);
      return response.data;
    }

    @Action
    async getLoadOrders(loadId): Promise<BarcodeOrderListItem[]> {
      const response = await rawApi.get<BarcodeOrderListItem[]>(`/barcode/loads/${loadId}/orders`);
      return response.data;
    }

    @Action 
    resetData() {
        this.resetList();
    }

    @Action
    async loadLoad(loadId: number): Promise<void> {
      const data = await this.getLoad(loadId);
      this.setCurrentLoad(data);
      this.refreshLoadData(data);
    }   
     
    @Action
    async loadLoadOrders(loadId: number): Promise<void> {
      this.setCurrentLoadOrders(await this.getLoadOrders(loadId));
    }
  
    @Action
    async reloadLoad(): Promise<void> {
      if (this.currentLoad)
      {
        const data = await this.getLoad(this.currentLoad.id);
        this.setCurrentLoad(data);
        this.refreshLoadData(data);
      } 
    }

    @Action
    async viewLoadShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.loadViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }    
    
    @Action
    async unlockLoadShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.unlockLoadModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }
    
    @Action
    async consolidateLoadsShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.consolidateLoadsModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async updateLoadsAfterAction(items: any) {
      for (let index = 0; index < items.length; index++) {
        const load = await this.getLoad(items[index].id);
        this.refreshLoadData(load)
      }
    }

    @Action
    async unlockLoad(load) {
      await api.postAsync(`barcode/loads/${load.id}/unlock`, load);
    }

    @Action
    async consolidateLoads(payload: ConsolidateLoadsRequest) {
      await api.postAsync(`barcode/loads/consolidate`, payload);
    }

    @Mutation
    setLoading(isLoading: boolean) {
        this.loading = isLoading;
    }

    @Mutation
    setFilters(filters: LoadListFilters)
    {
        this.filters = filters;
    }

    @Mutation
    setList(response: AxiosResponse<LoadListItem[]>) {
        this.pageData.totalItems = response.headers.totalitems;
        this.pageData.totalPages = response.headers.totalpages;
        if (this.data) {
            this.data = this.data.concat(response.data);
        } else {
            this.data = response.data;
        }
    }  
    
    @Mutation
    setCurrentPage(number: number) {
      this.pageData.currentPage = number;
    }

    @Mutation
    resetList() {
      this.pageData.totalItems = 0;
      this.pageData.totalPages = 0;
      this.data = null;
    }

    @Mutation
    switchSelectionStatus(id: number) {
      var index = this.data.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.data[index];

      existing.selected = !existing.selected;

      this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
    }  

    @Mutation
    switchLoadOrderSelectionStatus(id: number) {
      if(this.currentLoadOrders)
      {
        var index = this.currentLoadOrders.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentLoadOrders[index];

        existing.selected = !existing.selected;

        this.currentLoadOrders = [...this.currentLoadOrders.slice(0, index), existing, ...this.currentLoadOrders.slice(index + 1)];
      }    
    }

    @Mutation
    refreshLoadData(load: LoadListItem) {
      if(this.data)
      {
        var index = this.data.findIndex(function(item) {
            return item.id === load.id;
        });

        if (index < 0) {
            return;
        }

        this.data = [...this.data.slice(0, index), load, ...this.data.slice(index + 1)]
      }
    } 
    
    @Mutation
    setCurrentLoad(data) {
      this.loading = false;
      this.currentLoadOrders = [];
      this.currentLoad = data;
    }    
    
    @Mutation
    setCurrentLoadOrders(data) {
      this.loading = false;
      this.currentLoadOrders = data;
    }

    @Mutation
    resetCurrentLoad() {
      this.currentLoad = defaultLoad();
      this.currentLoadOrders = [];
    }

    @Mutation
    setFieldStatus(loadFieldChange) {
      this.fields[loadFieldChange.idx].hidden = loadFieldChange.value;
    }

    @Mutation
    setDataSort(key) {
      if (this.dataSortKey === key) this.dataSortAscending = !this.dataSortAscending;
      else {
        this.dataSortKey = key;
        this.dataSortAscending = true;
      }
    }
    

    get getSelectedItems(): Array<LoadListItem> {
        if (!this.data) return [];
        return this.data.filter(x => x.selected);
    }

    get getSelectedItemCount(): number {
        return this.getSelectedItems.length;
    }

    get getSelectedLoadOrders(): Array<BarcodeOrderListItem> {
      if (!this.currentLoadOrders) return [];
      return this.currentLoadOrders.filter(x => x.selected);
    }

    get getSelectedLoadOrderCount(): number {
      return this.getSelectedLoadOrders.length;
    }
}